/**
 * Fragmentador session types + create-video body compiler.
 */

import type {
  FragmentadorPlan,
  CameraMoveId,
  TransitionOutId,
  PaceId,
  EnergyId,
  PlanAllow,
  PlanRequirement,
} from './fragmentadorPromptContract';
import {
  assertPhasePrompts,
  assertPlanTiming,
  buildContinuousPrompt,
  buildVideoStoryboard,
  expandCameraMove,
  expandEnergy,
  expandPace,
  expandTransitionOut,
  isTransformationPlan,
  PHASE_COUNT_MAX,
  PHASE_COUNT_MIN,
  SECONDS_PER_PHASE,
  type ContinuousPromptOpts,
  type PhaseAssertIssue,
} from './fragmentadorPromptContract';

export type PhaseStatus = 'pending' | 'generating' | 'ready' | 'failed' | 'skipped';

export type FragmentadorSessionPhase = {
  id: string;
  label: string;
  role: 'start' | 'beat' | 'end';
  t_start: number;
  t_end: number;
  camera_move: CameraMoveId;
  transition_out: TransitionOutId;
  pace: PaceId;
  energy: EnergyId;
  description_pt: string;
  still_prompt_en: string;
  video_prompt_en: string;
  image_index: number;
  status: PhaseStatus;
  imageUrl?: string;
  jobId?: string;
  error?: string;
  action_en?: string;
  on_screen_text?: string[];
  requirement_ids?: string[];
};

/** What the user asked for, as planned (see FragmentadorPlan). */
export type SessionRequestPlan = {
  requirements?: PlanRequirement[];
  styleEn?: string;
  continuityEn?: string;
  avoidEn?: string;
  allow?: PlanAllow;
};

export type FragmentadorSession = SessionRequestPlan & {
  brief: string;
  startUrl: string;
  endUrl: string;
  imageModelId: string;
  videoModelId: string;
  resolutionId: string;
  durationSeconds: number;
  aspectRatio: '16:9' | '9:16' | '1:1' | '4:3' | '3:4';
  generateAudio: boolean;
  look: FragmentadorPlan['look'];
  intentHint: FragmentadorPlan['intent_hint'];
  phases: FragmentadorSessionPhase[];
  totalDurationSeconds: number;
  /** Indices i where edge phases[i]→phases[i+1] was removed (awaiting reconnect). */
  brokenEdgeAfter?: number[];
  videoJobId?: string;
  videoResultUrl?: string;
  /** Multi-clip: job ids per part (sequential create-video). */
  videoPartJobIds?: string[];
  /** Multi-clip: result URLs per part before stitch. */
  videoPartUrls?: string[];
  /** Multi-clip: planned chunks for the last generate. */
  videoChunks?: FragmentadorVideoChunk[];
};

export type FragmentadorVideoChunk = {
  phaseIds: string[];
  /** Inclusive start / exclusive end into the full phases array. */
  startIndex: number;
  endIndex: number;
  durationSeconds: number;
  /** True when we invented a coverage cut at the end of this chunk. */
  forcedCutOut?: boolean;
  /** True when we forced a new camera angle at the start of this chunk. */
  forcedCoverageIn?: boolean;
};

export const FRAGMENTADOR_SESSION_KEY = 'seu.video.fragmentadorSession';

/** Alternate camera for coverage cut between multi-clip parts. */
const COVERAGE_CAMERA_CYCLE: CameraMoveId[] = [
  'dolly_in',
  'crane',
  'orbit_right',
  'pan_right',
  'dolly_out',
  'tilt_up',
  'orbit_left',
  'tracking',
  'steadicam',
  'shake',
];

export function alternateCoverageCamera(prev: CameraMoveId): CameraMoveId {
  const idx = COVERAGE_CAMERA_CYCLE.indexOf(prev);
  for (let step = 1; step <= COVERAGE_CAMERA_CYCLE.length; step++) {
    const next = COVERAGE_CAMERA_CYCLE[(Math.max(0, idx) + step) % COVERAGE_CAMERA_CYCLE.length];
    if (next !== prev) return next;
  }
  return prev === 'crane' ? 'dolly_in' : 'crane';
}

function phaseDuration(p: FragmentadorSessionPhase): number {
  const d = Math.round(Number(p.t_end) - Number(p.t_start));
  return Number.isFinite(d) && d >= 1 ? d : SECONDS_PER_PHASE;
}

function uniqueRefCount(chunkPhases: FragmentadorSessionPhase[]): number {
  const set = new Set<string>();
  for (const p of chunkPhases) {
    if (p.imageUrl && /^https?:\/\//i.test(p.imageUrl)) set.add(p.imageUrl);
  }
  return set.size;
}

type ChunkPlanCost = [
  tooShort: number,
  forcedCuts: number,
  cutsInside: number,
  parts: number,
  longest: number,
];

function lessCost(a: ChunkPlanCost, b: ChunkPlanCost): boolean {
  for (let k = 0; k < a.length; k++) {
    if (a[k] !== b[k]) return a[k] < b[k];
  }
  return false;
}

/**
 * Split phases into disjoint chunks that fit model maxRefs + durationMax.
 * Each chunk uses only its own stills (no global end on early parts — that made every clip finish the build).
 * Hard cuts become part boundaries (models drift cut timing and dissolve inside one clip), so picks the split
 * with (in order): fewest parts under durationMin, fewest boundaries that are not natural cuts,
 * fewest natural cuts left inside a part, fewest parts, shortest longest part.
 */
export function chunkPhasesForModel(
  phases: FragmentadorSessionPhase[],
  opts: {
    maxReferenceImages: number;
    durationMax: number;
    durationMin?: number;
    startUrl?: string | null;
    endUrl?: string | null;
  },
): FragmentadorVideoChunk[] {
  const list = phases.map((p) => withDefaults(p));
  const n = list.length;
  if (!n) return [];

  const maxRefs = Math.max(2, Math.round(opts.maxReferenceImages) || 2);
  const durationMax = Math.max(1, Math.round(opts.durationMax) || 15);
  const durationMin = Math.max(1, Math.round(opts.durationMin ?? 1) || 1);
  const durs = list.map(phaseDuration);
  const isCut = (idx: number) => list[idx]?.transition_out === 'cut';

  const fits = (i: number, end: number) =>
    end - i === 1 ||
    (durs.slice(i, end).reduce((a, d) => a + d, 0) <= durationMax &&
      uniqueRefCount(list.slice(i, end)) <= maxRefs);

  const best: Array<{ cost: ChunkPlanCost; end: number } | null> = new Array(n + 1).fill(null);
  best[n] = { cost: [0, 0, 0, 0, 0], end: n };
  for (let i = n - 1; i >= 0; i--) {
    for (let end = i + 1; end <= n; end++) {
      if (!fits(i, end)) break;
      const rest = best[end]!;
      const dur = durs.slice(i, end).reduce((a, d) => a + d, 0);
      const forced = end < n && !isCut(end - 1) ? 1 : 0;
      let inside = 0;
      for (let k = i; k < end - 1; k++) if (isCut(k)) inside += 1;
      const cost: ChunkPlanCost = [
        rest.cost[0] + (dur < durationMin ? 1 : 0),
        rest.cost[1] + forced,
        rest.cost[2] + inside,
        rest.cost[3] + 1,
        Math.max(rest.cost[4], dur),
      ];
      if (!best[i] || lessCost(cost, best[i]!.cost)) best[i] = { cost, end };
    }
  }

  const chunks: FragmentadorVideoChunk[] = [];
  for (let i = 0; i < n; ) {
    const end = best[i]!.end;
    const slice = list.slice(i, end);
    const naturalCut = end < n && list[end - 1]?.transition_out === 'cut';
    chunks.push({
      phaseIds: slice.map((p) => p.id),
      startIndex: i,
      endIndex: end,
      durationSeconds: durs.slice(i, end).reduce((a, d) => a + d, 0),
      forcedCutOut: end < n && !naturalCut,
      forcedCoverageIn: i > 0 && !isCut(i - 1),
    });
    i = end;
  }
  return chunks;
}

export function forceCoverageCutAtBoundary(
  chunkPhases: FragmentadorSessionPhase[],
  meta: { forcedCutOut?: boolean; forcedCoverageIn?: boolean; prevCamera?: CameraMoveId },
): FragmentadorSessionPhase[] {
  if (!chunkPhases.length) return chunkPhases;
  const next = chunkPhases.map((p) => ({ ...p }));
  if (meta.forcedCoverageIn) {
    const first = next[0];
    const prevCam = meta.prevCamera || first.camera_move;
    const cam = alternateCoverageCamera(prevCam);
    const opener =
      'Cut to a new camera angle and framing. Fresh coverage shot — do not continue the previous take. ';
    first.camera_move = cam;
    first.video_prompt_en = `${opener}${first.video_prompt_en}`.trim();
    if (first.video_prompt_en.length > 512) {
      first.video_prompt_en = first.video_prompt_en.slice(0, 512).trim();
    }
  }
  if (meta.forcedCutOut && next.length) {
    const last = next[next.length - 1];
    last.transition_out = 'cut';
    const closer = ' Settle briefly, then hard cut.';
    if (!/hard cut|settle briefly/i.test(last.video_prompt_en)) {
      last.video_prompt_en = `${last.video_prompt_en}${closer}`.trim();
      if (last.video_prompt_en.length > 512) {
        last.video_prompt_en = last.video_prompt_en.slice(0, 512).trim();
      }
    }
  }
  return next;
}

function remapImageTokens(prompt: string, indexMap: Map<number, number>): string {
  return prompt.replace(/<<<image_(\d+)>>>/g, (full, n) => {
    const old = Number(n);
    const mapped = indexMap.get(old);
    if (mapped == null) return full;
    return `<<<image_${mapped}>>>`;
  });
}

/** Continuous takes must open on the uploaded still, not a redraw of it. */
export function openingFrameClause(token: string): string {
  return (
    `Use ${token} as the exact first frame of the video. ` +
    `Do not redraw, reinterpret, or restyle the opening image. ` +
    `Begin the motion from that exact image, with no initial pop, flicker, or sudden repositioning. `
  );
}

export function buildIdentityLockClause(
  startToken: string,
  endToken: string,
  opts?: { transformation?: boolean; hasActions?: boolean; cameraOnly?: boolean },
): string {
  // Camera path (FPV, facade flight): "face" / "person" makes the model hunt for someone who is not in frame.
  if (opts?.cameraOnly && !opts.transformation) {
    const span = endToken && endToken !== startToken ? `from ${startToken} through ${endToken}` : `as ${startToken}`;
    return (
      `Identity lock: same place and structure ${span}. ` +
      `Preserve geometry, proportions, colors and materials; only the camera moves. `
    );
  }
  if (opts?.hasActions && !opts.transformation) {
    const span = endToken && endToken !== startToken ? `from ${startToken} through ${endToken}` : `as ${startToken}`;
    return (
      `Identity lock: same place and pictured subject ${span}. ` +
      `Preserve face, proportions, colors and structure; the camera and the actions described below are what change. `
    );
  }
  if (opts?.transformation && endToken && endToken !== startToken) {
    return (
      `Continuity lock: one unbroken physical transformation from ${startToken} through ${endToken}. ` +
      `Keep each checkpoint's exact look (colors, text, logos, shapes); never redesign a checkpoint. `
    );
  }
  // create-video's resolveMotionIntent regex-scans the prompt (staging|constru|evolu|transform…):
  // camera-only wording must avoid those words or the clip is forced into progressive_build.
  if (endToken && endToken !== startToken) {
    return (
      `Identity lock: same person, product, materials and place from ${startToken} through ${endToken}. ` +
      `Preserve face, proportions, colors and structure; only the camera angle and framing change. `
    );
  }
  return (
    `Identity lock: same person, product, materials and place as ${startToken}. ` +
    `Preserve face, proportions, colors and structure; only the camera angle and framing change. `
  );
}

/** Scope one multiclip part so early segments do not complete the full build. */
export function buildSegmentScopeClause(opts: {
  partIndex: number;
  partCount: number;
  firstTok: string;
  lastTok: string;
  transformation?: boolean;
}): string {
  const partCount = Math.max(1, Math.round(opts.partCount) || 1);
  const partIndex = Math.min(Math.max(0, Math.round(opts.partIndex) || 0), partCount - 1);
  if (partCount <= 1) return '';
  const isLast = partIndex === partCount - 1;
  if (!opts.transformation && opts.firstTok === opts.lastTok) {
    return `Segment ${partIndex + 1}/${partCount}: this clip shows ONLY ${opts.firstTok}; do not preview other scenes. `;
  }
  if (!opts.transformation) {
    return (
      `Segment ${partIndex + 1}/${partCount}: covers ONLY ${opts.firstTok} through ${opts.lastTok}; ` +
      `start on ${opts.firstTok}, end on ${opts.lastTok}; do not preview scenes outside this segment. `
    );
  }
  if (isLast) {
    return (
      `Final segment (${partIndex + 1}/${partCount}): continue from ${opts.firstTok} and reach completion only at ${opts.lastTok}. ` +
      `This part alone may show the fully finished look. `
    );
  }
  return (
    `Segment ${partIndex + 1}/${partCount}: animate ONLY from ${opts.firstTok} through ${opts.lastTok}. ` +
    `End this part still incomplete at ${opts.lastTok} — do NOT reveal the fully finished look; later segments continue the build. `
  );
}

/**
 * Compile one multi-clip part: local timing, segment-local refs, coverage boundary tweaks.
 */
export function compileCreateVideoBodyForChunk(args: {
  organizationId: string;
  session: FragmentadorSession;
  chunk: FragmentadorVideoChunk;
  prevChunkLastCamera?: CameraMoveId;
  partIndex?: number;
  partCount?: number;
  /** Contínuo keeps the uploaded opening still. Reference mode does not. */
  lockOpening?: boolean;
}): CreateVideoCompileResult {
  const { organizationId, session, chunk } = args;
  const all = session.phases.map((p) => withDefaults(p));
  const partCount = Math.max(1, Math.round(args.partCount ?? 1) || 1);
  const partIndex = Math.min(
    Math.max(0, Math.round(args.partIndex ?? 0) || 0),
    partCount - 1,
  );
  const isFinalPart = partIndex >= partCount - 1;

  let slice = all.slice(chunk.startIndex, chunk.endIndex).map((p) => ({ ...p }));
  slice = forceCoverageCutAtBoundary(slice, {
    forcedCutOut: chunk.forcedCutOut,
    forcedCoverageIn: chunk.forcedCoverageIn,
    prevCamera: args.prevChunkLastCamera,
  });

  // Retime locally to 0..duration
  let t = 0;
  slice = slice.map((p, i) => {
    const dur = phaseDuration(p);
    const phase: FragmentadorSessionPhase = {
      ...p,
      role: i === 0 ? 'start' : i === slice.length - 1 ? 'end' : 'beat',
      t_start: t,
      t_end: t + dur,
    };
    t += dur;
    return phase;
  });

  // Only this segment's stills — never inject the global end onto early parts
  // (that made every clip animate to the finished building).
  const refUrls: string[] = [];
  const pushUrl = (u?: string | null) => {
    if (!u || !/^https?:\/\//i.test(u)) return;
    if (refUrls.includes(u)) return;
    refUrls.push(u);
  };
  for (const p of slice) pushUrl(p.imageUrl);

  // Map global image_index → local ref index (1-based by URL)
  const urlToLocal = new Map<string, number>();
  refUrls.forEach((u, i) => urlToLocal.set(u, i + 1));

  const globalIndexToLocal = new Map<number, number>();
  for (const p of all) {
    if (p.imageUrl && urlToLocal.has(p.imageUrl)) {
      globalIndexToLocal.set(p.image_index, urlToLocal.get(p.imageUrl)!);
    }
  }
  // Also map by position in full list for tokens that used sequential indices
  all.forEach((p, i) => {
    if (p.imageUrl && urlToLocal.has(p.imageUrl)) {
      globalIndexToLocal.set(i + 1, urlToLocal.get(p.imageUrl)!);
    }
  });

  slice = slice.map((p) => {
    const localIdx = p.imageUrl ? urlToLocal.get(p.imageUrl) || 1 : 1;
    return {
      ...p,
      image_index: localIdx,
      video_prompt_en: remapImageTokens(p.video_prompt_en, globalIndexToLocal),
      ...(p.action_en ? { action_en: remapImageTokens(p.action_en, globalIndexToLocal) } : {}),
    };
  });

  // Ensure each prompt cites its local token
  slice = slice.map((p) => {
    const token = `<<<image_${p.image_index}>>>`;
    let video = p.video_prompt_en;
    if (!video.includes(token)) {
      video = `Match ${token}. ${video}`.trim();
    }
    return { ...p, video_prompt_en: video };
  });

  const transformation = isTransformationPlan(session.intentHint, slice);
  const phaseNumbers = Array.from({ length: chunk.endIndex - chunk.startIndex }, (_, i) => chunk.startIndex + i + 1);
  const requested = requestPlanFor(session, phaseNumbers);
  const issues: PhaseAssertIssue[] = [];
  if (!allKeyframesReady(slice)) {
    issues.push({ field: 'phases', message: 'Todas as keyframes precisam estar prontas.' });
  } else {
    const total = slice[slice.length - 1]?.t_end ?? 0;
    // Plan-level phase count does not apply to one part: a single-scene part is valid.
    issues.push(...assertPlanTiming(slice, total).filter((x) => x.field !== 'phases'));
    // image_index may point into the expanded ref list (anchors + beats)
    for (const p of slice) {
      issues.push(
        ...assertPhasePrompts(p, refUrls.length, { requireStill: false, transformation, allow: session.allow }),
      );
    }
  }
  const storyboard = buildVideoStoryboard(slice);
  const startTok = `<<<image_1>>>`;
  const endTok = `<<<image_${Math.max(1, refUrls.length)}>>>`;
  let continuousPrompt = buildContinuousPrompt(slice, { transformation, ...requested.promptOpts });
  const hasActions = slice.some((p) => Boolean(p.action_en || p.on_screen_text?.length));
  const opening = args.lockOpening === false ? '' : openingFrameClause(startTok);
  continuousPrompt =
    `${opening}${buildSegmentScopeClause({
      partIndex,
      partCount,
      firstTok: startTok,
      lastTok: endTok,
      transformation,
    })}${buildIdentityLockClause(startTok, endTok, {
      transformation,
      hasActions,
      cameraOnly: session.intentHint === 'camera_showcase',
    })}${continuousPrompt}`.trim();

  const reference_images = refUrls.map((url, i) => ({
    url,
    role: i === 0 ? 'start' : i === refUrls.length - 1 ? 'end' : 'ref',
    label: i === 0 ? 'segment_start' : i === refUrls.length - 1 ? 'segment_end' : `beat_${i}`,
  }));

  const tags =
    session.look === 'cinematic'
      ? ['style_cinematic', 'dist_medium']
      : ['style_realistic', 'dist_medium'];

  if (session.intentHint === 'camera_showcase') {
    tags.push('cam_guided');
  }
  if (session.intentHint === 'progressive_build' || session.intentHint === 'subject_evolution') {
    tags.push('trans_evolution');
  }
  // Multi-clip parts always allow coverage cuts at boundaries — never cons_no_cut across parts.
  const cutCount = slice.filter((p, i) => i < slice.length - 1 && p.transition_out === 'cut').length;
  const mostlyCuts =
    cutCount > Math.max(1, Math.floor((slice.length - 1) / 2)) ||
    Boolean(chunk.forcedCutOut) ||
    Boolean(chunk.forcedCoverageIn) ||
    session.intentHint === 'cinematic_montage';
  if (mostlyCuts) {
    tags.push('cinematic_montage');
  } else {
    tags.push('timing_continuous');
  }

  const durationSeconds = chunk.durationSeconds || t;
  const body: Record<string, unknown> = {
    organization_id: organizationId,
    duration_seconds: durationSeconds,
    aspect_ratio: session.aspectRatio,
    storyboard,
    reference_images,
    tags,
    prompt: continuousPrompt,
    video_model_id: session.videoModelId,
    resolution_id: session.resolutionId,
    feature: 'staging',
    generate_audio: Boolean(session.generateAudio),
    scene_config: {
      scene_reference: continuousPrompt,
      camera_directives: slice
        .map((p, i) => {
          const cam = expandCameraMove(p.camera_move as CameraMoveId);
          const pace = expandPace(p.pace);
          const energy = expandEnergy(p.energy);
          const bridge =
            i === slice.length - 1
              ? 'settle on the final frame (clip ends here)'
              : p.transition_out === 'cut'
              ? expandTransitionOut('cut')
              : expandTransitionOut('camera');
          return `${p.t_start}-${p.t_end}s: ${cam}; ${pace}; ${energy}; then ${bridge}`;
        })
        .join(' | '),
      transitions: slice.slice(0, -1).map((p, i) => ({
        after_phase: i + 1,
        type: p.transition_out,
      })),
      pace_map: slice.map((p) => ({ phase: p.image_index, pace: p.pace, energy: p.energy })),
      multi_clip: partCount > 1,
      part_index: partIndex,
      part_count: partCount,
      is_final_part: isFinalPart,
      chunk_start_index: chunk.startIndex,
      chunk_end_index: chunk.endIndex,
      ...requested.sceneConfig,
    },
  };

  return { body, continuousPrompt, storyboard, issues };
}

/** How many create-video parts this model needs for the current plan. */
export function countChunksForModel(
  phases: FragmentadorSessionPhase[],
  model: { maxReferenceImages: number; durationMax: number; durationMin?: number },
  startUrl?: string | null,
  endUrl?: string | null,
): number {
  return chunkPhasesForModel(phases, {
    maxReferenceImages: model.maxReferenceImages,
    durationMax: model.durationMax,
    durationMin: model.durationMin,
    startUrl,
    endUrl,
  }).length;
}

function withDefaults(p: Partial<FragmentadorSessionPhase> & { id: string }): FragmentadorSessionPhase {
  return {
    label: p.label || 'Fase',
    role: p.role || 'beat',
    t_start: p.t_start ?? 0,
    t_end: p.t_end ?? 2,
    camera_move: p.camera_move || 'steadicam',
    transition_out: p.transition_out || 'camera',
    pace: p.pace || 'normal',
    energy: p.energy || 'normal',
    description_pt: p.description_pt || '',
    still_prompt_en: p.still_prompt_en || '',
    video_prompt_en: p.video_prompt_en || '',
    image_index: p.image_index || 1,
    status: p.status || 'pending',
    imageUrl: p.imageUrl,
    jobId: p.jobId,
    error: p.error,
    id: p.id,
    ...(p.action_en ? { action_en: p.action_en } : {}),
    ...(p.on_screen_text?.length ? { on_screen_text: p.on_screen_text } : {}),
    ...(p.requirement_ids?.length ? { requirement_ids: p.requirement_ids } : {}),
  };
}

/** Prompt options + scene_config fields for the user's requests (requirements scoped to these phases). */
function requestPlanFor(
  session: FragmentadorSession,
  phaseNumbers: number[],
): {
  promptOpts: Pick<ContinuousPromptOpts, 'requirements' | 'style_en' | 'continuity_en' | 'avoid_en' | 'allow'>;
  sceneConfig: Record<string, unknown>;
} {
  const all = session.requirements || [];
  const inScope = all.filter((r) => r.scope === 'global' || r.scope.some((n) => phaseNumbers.includes(n)));
  const requirements = inScope.length ? inScope : all;
  const promptOpts = {
    requirements,
    style_en: session.styleEn,
    continuity_en: session.continuityEn,
    avoid_en: session.avoidEn,
    allow: session.allow,
  };
  if (!requirements.length) return { promptOpts, sceneConfig: {} };
  return {
    promptOpts,
    sceneConfig: {
      requirements: requirements.map((r) => ({ id: r.id, text_pt: r.text_pt })),
      allow: session.allow || { text_graphics: false, aircraft: false, stylized: false },
      ...(session.styleEn ? { style_en: session.styleEn } : {}),
      ...(session.continuityEn ? { continuity_en: session.continuityEn } : {}),
      ...(session.avoidEn ? { avoid_en: session.avoidEn } : {}),
    },
  };
}

export function planToSessionPhases(
  plan: FragmentadorPlan,
  startUrl: string,
  endUrl: string,
): FragmentadorSessionPhase[] {
  return plan.phases.map((p) => {
    const base = withDefaults({ ...p, status: 'pending' });
    const isStartWithPhoto = p.role === 'start' && Boolean(startUrl);
    const isEndWithPhoto = p.role === 'end' && Boolean(endUrl);
    if (isStartWithPhoto) {
      return { ...base, status: 'ready' as const, imageUrl: startUrl };
    }
    if (isEndWithPhoto) {
      return { ...base, status: 'ready' as const, imageUrl: endUrl };
    }
    return base;
  });
}

/**
 * Refresh prompts/camera/timing from a new plan.
 * Prefer Gemini's reuse_from_index (1-based into the previous plan) so mids that
 * still match keep their stills and skip image credits. Always pin start/end
 * photo anchors. Never slide the old end still onto a new mid beat.
 */
export function mergePlanOntoPhases(
  plan: FragmentadorPlan,
  existing: FragmentadorSessionPhase[],
): FragmentadorSessionPhase[] {
  const countChanged = plan.phases.length !== existing.length;
  const startUrl = existing[0]?.imageUrl;
  const endUrl =
    existing.length > 1 ? existing[existing.length - 1]?.imageUrl : undefined;

  return plan.phases.map((p, i) => {
    const isFirst = i === 0;
    const isLast = i === plan.phases.length - 1;
    const prevById = p.id ? existing.find((e) => e.id === p.id) : undefined;
    const prevByIndex = existing[i];
    const reuseIdx =
      typeof p.reuse_from_index === 'number' &&
      p.reuse_from_index >= 1 &&
      p.reuse_from_index <= existing.length
        ? p.reuse_from_index - 1
        : -1;
    const reused = reuseIdx >= 0 ? existing[reuseIdx] : undefined;

    let keptUrl: string | undefined;
    let keptJobId: string | undefined;
    let keptId: string | undefined;

    if (isFirst && startUrl) {
      keptUrl = startUrl;
      keptJobId = existing[0]?.jobId;
      keptId = existing[0]?.id;
    } else if (isLast && endUrl && plan.phases.length > 1) {
      keptUrl = endUrl;
      keptJobId = existing[existing.length - 1]?.jobId;
      keptId = existing[existing.length - 1]?.id;
    } else if (reused?.imageUrl) {
      keptUrl = reused.imageUrl;
      keptJobId = reused.jobId;
      keptId = reused.id;
    } else if (!countChanged) {
      const prev = prevById || prevByIndex;
      keptUrl = prev?.imageUrl;
      keptJobId = prev?.jobId;
      keptId = prev?.id;
    }

    return withDefaults({
      ...p,
      id: keptId || p.id,
      status: keptUrl ? ('ready' as const) : ('pending' as const),
      imageUrl: keptUrl,
      jobId: keptUrl ? keptJobId : undefined,
      error: undefined,
    });
  });
}

export function retimePhases(
  phases: FragmentadorSessionPhase[],
  totalDurationSeconds?: number,
): { phases: FragmentadorSessionPhase[]; totalDurationSeconds: number } {
  const n = Math.max(PHASE_COUNT_MIN, Math.min(PHASE_COUNT_MAX, phases.length || 2));
  const sliced = phases.slice(0, n).map((p) => withDefaults(p));
  // Preserve each phase's own duration (Gemini or prior edit); only fall back to default for empty spans.
  const durs = sliced.map((p) => {
    const d = Math.round(Number(p.t_end) - Number(p.t_start));
    return Number.isFinite(d) && d >= 1 ? d : SECONDS_PER_PHASE;
  });
  let sum = durs.reduce((a, b) => a + b, 0);
  if (totalDurationSeconds && totalDurationSeconds > 0 && totalDurationSeconds !== sum && sum > 0) {
    const target = Math.round(totalDurationSeconds);
    const scale = target / sum;
    let t = 0;
    const next = sliced.map((p, i) => {
      const dur = i === n - 1 ? Math.max(1, target - t) : Math.max(1, Math.round(durs[i] * scale));
      const phase: FragmentadorSessionPhase = {
        ...p,
        role: i === 0 ? 'start' : i === n - 1 ? 'end' : 'beat',
        image_index: i + 1,
        t_start: t,
        t_end: t + dur,
        label: p.label || `Fase ${i + 1}`,
        transition_out: i === n - 1 ? 'camera' : p.transition_out || 'camera',
      };
      t += dur;
      return phase;
    });
    if (next.length) next[next.length - 1].t_end = target;
    return { phases: next, totalDurationSeconds: target };
  }
  let t = 0;
  const next = sliced.map((p, i) => {
    const dur = durs[i];
    const phase: FragmentadorSessionPhase = {
      ...p,
      role: i === 0 ? 'start' : i === n - 1 ? 'end' : 'beat',
      image_index: i + 1,
      t_start: t,
      t_end: t + dur,
      label: p.label || `Fase ${i + 1}`,
      transition_out: i === n - 1 ? 'camera' : p.transition_out || 'camera',
    };
    t += dur;
    return phase;
  });
  return { phases: next, totalDurationSeconds: t };
}

export function createEmptyPhase(index: number): FragmentadorSessionPhase {
  return withDefaults({
    id: `phase_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    label: `Fase ${index + 1}`,
    role: 'beat',
    t_start: 0,
    t_end: 2,
    camera_move: 'steadicam',
    transition_out: 'camera',
    pace: 'normal',
    energy: 'normal',
    description_pt: 'Nova cena',
    still_prompt_en:
      'Photoreal medium shot of the same subject and environment as the reference, clear framing, natural daylight, sharp materials, no illustration.',
    video_prompt_en:
      'Medium shot. Subject holds steady. Camera moves smoothly matching <<<image_1>>>. Natural soft daylight, photoreal look.',
    image_index: index + 1,
    status: 'pending',
  });
}

export function nextPendingPhaseIndex(phases: FragmentadorSessionPhase[]): number {
  return phases.findIndex((p) => p.status === 'pending' || p.status === 'failed');
}

export type PhaseImageRefRole = 'before' | 'after' | 'start' | 'end';
export type PhaseImageRef = { url: string; role: PhaseImageRefRole };

/** Max refs sent to the image model per keyframe (create-preview accepts up to 4). */
const KEYFRAME_REF_MAX = 3;

/**
 * Refs for i2i: nearest ready image before AND after this scene (so a keyframe placed before
 * a user photo still sees it), then the flow's start/end anchors when there is room.
 */
export function collectPhaseImageRefs(
  phases: FragmentadorSessionPhase[],
  phaseIndex: number,
  startUrl?: string | null,
  endUrl?: string | null,
): PhaseImageRef[] {
  const isHttp = (u?: string | null): u is string => Boolean(u && /^https?:\/\//i.test(u));
  const ready = (p?: FragmentadorSessionPhase) => Boolean(p && isHttp(p.imageUrl) && p.status === 'ready');
  const n = Math.max(1, phases.length - 1);
  const progress = phaseIndex / n; // 0 = first, 1 = last
  const prev = phases.slice(0, phaseIndex).reverse().find(ready);
  const next = phases.slice(phaseIndex + 1).find(ready);

  const ordered: Array<{ url?: string | null; role: PhaseImageRefRole }> = [
    { url: prev?.imageUrl, role: 'before' },
    { url: next?.imageUrl, role: 'after' },
  ];
  // End anchor only late — early beats must not copy the finished look.
  if (progress < 0.7) ordered.push({ url: startUrl, role: 'start' });
  if (progress >= 0.65) ordered.push({ url: endUrl, role: 'end' });

  const out: PhaseImageRef[] = [];
  for (const { url, role } of ordered) {
    if (!isHttp(url) || out.some((r) => r.url === url)) continue;
    out.push({ url, role });
    if (out.length >= KEYFRAME_REF_MAX) break;
  }
  return out;
}

const REF_ROLE_TEXT: Record<PhaseImageRefRole, string> = {
  before: 'the scene right BEFORE this one',
  after: 'the scene right AFTER this one',
  start: 'the opening scene of the video',
  end: 'the final scene of the video',
};

/** Prompt suffix: says what each reference is, keeps identity, forces distinct framing, optional stage lock. */
export function buildKeyframeIdentityClause(
  refs: PhaseImageRef[],
  phaseIndex = 0,
  phaseCount = 1,
  intentHint?: string,
): string {
  if (!refs.length) return '';
  const n = Math.max(1, phaseCount);
  const progress = n <= 1 ? 1 : phaseIndex / Math.max(1, n - 1);
  const evolving =
    intentHint === 'progressive_build' ||
    intentHint === 'subject_evolution' ||
    intentHint === 'kinetic_assembly';

  const roles =
    ' Reference images, in order: ' +
    refs.map((r, i) => `#${i + 1} = ${REF_ROLE_TEXT[r.role]}`).join('; ') +
    '.';
  const hasBefore = refs.some((r) => r.role === 'before');
  const hasAfter = refs.some((r) => r.role === 'after');
  const bridge =
    hasBefore && hasAfter
      ? ' This still is the moment BETWEEN them: continue naturally from the BEFORE scene and lead toward the AFTER scene.'
      : hasAfter
      ? ' This still happens BEFORE the AFTER scene and must lead naturally into it (same world, light and palette).'
      : hasBefore
      ? ' This still continues naturally from the BEFORE scene.'
      : '';

  if (!evolving) {
    return (
      roles +
      bridge +
      ' Same subject and place as the reference(s).' +
      ' Preserve recognizable identity, materials and colors, but this still MUST use a clearly different camera angle, height or distance than the reference photos — do not copy the same framing.' +
      ' Do not invent a different product, person, location or design.'
    );
  }

  const stageLock =
    progress >= 0.92
      ? ' This still is the FINAL stage — match the finished reference completeness.'
      : progress >= 0.55
      ? ' This still is MID-LATE: clearly less complete than the finished end reference — partial progress only, not the final look.'
      : ' This still is EARLY: far less complete than the finished end reference; do not invent finished details from the end photo.';
  return (
    roles +
    bridge +
    ' Same subject / project as the reference(s).' +
    ' Preserve recognizable materials and identity, but this still MUST show a DIFFERENT stage AND a clearly different camera angle, height or distance than the reference photos — do not copy the same framing.' +
    stageLock
  );
}

export function allKeyframesReady(phases: FragmentadorSessionPhase[]): boolean {
  return phases.length > 0 && phases.every((p) => p.status === 'ready' && Boolean(p.imageUrl));
}

export function validateSessionForVideo(
  phases: FragmentadorSessionPhase[],
  intentHint?: FragmentadorSession['intentHint'],
  allow?: PlanAllow,
): PhaseAssertIssue[] {
  const issues: PhaseAssertIssue[] = [];
  if (!allKeyframesReady(phases)) {
    issues.push({ field: 'phases', message: 'Todas as keyframes precisam estar prontas.' });
    return issues;
  }
  const total = phases[phases.length - 1]?.t_end ?? 0;
  issues.push(...assertPlanTiming(phases, total));
  const transformation = isTransformationPlan(intentHint, phases);
  for (const p of phases) {
    issues.push(...assertPhasePrompts(p, phases.length, { requireStill: false, transformation, allow }));
  }
  return issues;
}

export type CreateVideoCompileResult = {
  body: Record<string, unknown>;
  continuousPrompt: string;
  storyboard: ReturnType<typeof buildVideoStoryboard>;
  issues: PhaseAssertIssue[];
};

export function compileCreateVideoBody(args: {
  organizationId: string;
  session: FragmentadorSession;
}): CreateVideoCompileResult {
  const { organizationId, session } = args;
  const phases = session.phases.map((p) => withDefaults(p));
  const issues = validateSessionForVideo(phases, session.intentHint, session.allow);
  const storyboard = buildVideoStoryboard(phases);
  const requested = requestPlanFor(session, phases.map((_, i) => i + 1));
  const continuousPrompt = buildContinuousPrompt(phases, {
    transformation: isTransformationPlan(session.intentHint, phases),
    ...requested.promptOpts,
  });
  const hasCuts = phases.some((p, i) => i < phases.length - 1 && p.transition_out === 'cut');

  const reference_images = phases.map((p, i) => ({
    url: p.imageUrl,
    role: i === 0 ? 'start' : i === phases.length - 1 ? 'end' : 'ref',
    label: p.label,
  }));

  const tags =
    session.look === 'cinematic'
      ? ['style_cinematic', 'dist_medium']
      : ['style_realistic', 'dist_medium'];

  if (session.intentHint === 'camera_showcase') {
    tags.push('cam_guided', 'timing_continuous', 'cons_no_cut');
  }
  if (session.intentHint === 'progressive_build' || session.intentHint === 'subject_evolution') {
    tags.push('trans_evolution', 'timing_continuous', 'cons_no_cut');
  }
  // Prefer continuous flight unless the plan is mostly hard cuts (montage).
  const cutCount = phases.filter((p, i) => i < phases.length - 1 && p.transition_out === 'cut').length;
  const mostlyCuts = cutCount > Math.max(1, Math.floor((phases.length - 1) / 2));
  if (mostlyCuts || session.intentHint === 'cinematic_montage') {
    tags.push('cinematic_montage');
  } else if (hasCuts) {
    // Sparse cuts: still favor continuous take language in create-video.
    tags.push('timing_continuous');
  }

  const body: Record<string, unknown> = {
    organization_id: organizationId,
    duration_seconds: session.totalDurationSeconds || session.durationSeconds,
    aspect_ratio: session.aspectRatio,
    storyboard,
    reference_images,
    tags,
    prompt: continuousPrompt,
    video_model_id: session.videoModelId,
    resolution_id: session.resolutionId,
    feature: 'staging',
    generate_audio: Boolean(session.generateAudio),
    scene_config: {
      scene_reference: continuousPrompt,
      camera_directives: phases
        .map((p, i) => {
          const cam = expandCameraMove(p.camera_move as CameraMoveId);
          const pace = expandPace(p.pace);
          const energy = expandEnergy(p.energy);
          const bridge =
            i === phases.length - 1
              ? 'settle on the final frame (clip ends here)'
              : p.transition_out === 'cut'
              ? expandTransitionOut('cut')
              : expandTransitionOut('camera');
          return `${p.t_start}-${p.t_end}s: ${cam}; ${pace}; ${energy}; then ${bridge}`;
        })
        .join(' | '),
      transitions: phases.slice(0, -1).map((p, i) => ({
        after_phase: i + 1,
        type: p.transition_out,
      })),
      pace_map: phases.map((p) => ({ phase: p.image_index, pace: p.pace, energy: p.energy })),
      ...requested.sceneConfig,
    },
  };

  return { body, continuousPrompt, storyboard, issues };
}

export function loadSession(): FragmentadorSession | null {
  try {
    const raw = localStorage.getItem(FRAGMENTADOR_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FragmentadorSession;
    if (!parsed?.phases?.length) return parsed;
    parsed.phases = parsed.phases.map((p) => {
      const healed = withDefaults(p);
      if (healed.imageUrl && (healed.status === 'generating' || healed.error === 'Pronto')) {
        return { ...healed, status: 'ready' as const, error: undefined };
      }
      return healed;
    });
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: FragmentadorSession | null) {
  try {
    if (!session) {
      localStorage.removeItem(FRAGMENTADOR_SESSION_KEY);
      return;
    }
    localStorage.setItem(FRAGMENTADOR_SESSION_KEY, JSON.stringify(session));
  } catch {
    /* private mode */
  }
}
